import {
  type CamelizedType,
  formatData,
  getColumnsWithTransformedValues,
  transformObjectToCamelCase,
} from '@utilities';
import type {
  CamelizedTableDataPayloadColumn,
  TableDataPayload,
} from '@utilities/components/TableData/utilities/types';

interface PayloadItemWithTable {
  table?: TableDataPayload;
}

type CamelizedPayloadItemWithTable<T extends PayloadItemWithTable> = CamelizedType<T> & {
  table?: {
    columns: CamelizedTableDataPayloadColumn[];
    data: Record<string, unknown>[];
  };
};

type CamelizedPayloadWithTable<T extends PayloadItemWithTable> = CamelizedType<{
  data: T[];
}> & {
  data: CamelizedPayloadItemWithTable<T>[];
};

interface PayloadWithTable<T extends PayloadItemWithTable> {
  data: T[];
}

/**
 * camel-cases the payload and normalizes API table columns for TableData.
 */
export const camelizeData = <T extends PayloadItemWithTable>(data: PayloadWithTable<T>) => {
  const transformedData = transformObjectToCamelCase(data) as CamelizedPayloadWithTable<T>;
  return {
    ...transformedData,
    data: transformedData?.data?.map(({ table, ...item }) => ({
      ...item,
      ...(table && {
        table: {
          ...table,
          columns: camelizeTableColumns(table.columns),
        },
      }),
    })),
  };
};

/**
 * updates columns with the fields the TableData components expects
 */
const camelizeTableColumns = (columns: CamelizedTableDataPayloadColumn[] = []) =>
  getColumnsWithTransformedValues(columns).map(({ footer, footerFormat, ...column }) => ({
    ...column,
    ...(footer !== null &&
      footer !== undefined && {
        Footer: footerFormat ? formatData({ format: footerFormat, value: footer }) : footer,
      }),
  }));
